import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const EMPTY = {
  name: "", client: "", aircraft_type: "", aircraft_registration: "",
  location: "", start_date: "", end_date: "", status: "active", notes: "", assigned_employee_ids: [],
};

export default function Projects() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const reload = async () => {
    const [p, u] = await Promise.all([api.get("/projects"), api.get("/users")]);
    setProjects(p.data);
    setEmployees(u.data.filter((e) => e.role === "employee"));
  };

  useEffect(() => { reload(); }, []);

  const submit = async () => {
    try {
      if (editing) await api.patch(`/projects/${editing}`, form);
      else await api.post("/projects", form);
      toast.success(editing ? "Project updated" : "Project created");
      setOpen(false); setForm(EMPTY); setEditing(null);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const startEdit = (p) => {
    setForm({ ...EMPTY, ...p });
    setEditing(p.id);
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleEmployee = (uid) => {
    const list = form.assigned_employee_ids || [];
    setForm({
      ...form,
      assigned_employee_ids: list.includes(uid) ? list.filter((x) => x !== uid) : [...list, uid],
    });
  };

  const statusColor = (s) => s === "active" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
    : s === "on-hold" ? "bg-amber-500/10 text-amber-400 border-amber-500/30"
    : "bg-[#222] text-[#999] border-[#333]";

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">PROJECTS</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight mt-1">Operations</h1>
        </div>
        {user.role === "admin" && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setEditing(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="new-project-btn" className="rounded-none btn-brand border border-[#3b2bd0] font-heading uppercase tracking-[0.1em]">
                <Plus className="h-4 w-4 mr-2" /> New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0a0a0a] border-[#222] rounded-none max-w-2xl text-white">
              <DialogHeader>
                <DialogTitle className="font-heading uppercase tracking-tight">{editing ? "Edit Project" : "New Project"}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
                <Field label="Name" value={form.name} set={(v) => setForm({ ...form, name: v })} testid="proj-name" />
                <Field label="Client" value={form.client} set={(v) => setForm({ ...form, client: v })} testid="proj-client" />
                <Field label="Aircraft Type" value={form.aircraft_type} set={(v) => setForm({ ...form, aircraft_type: v })} testid="proj-actype" />
                <Field label="Aircraft Reg." value={form.aircraft_registration} set={(v) => setForm({ ...form, aircraft_registration: v })} testid="proj-reg" />
                <Field label="Location" value={form.location} set={(v) => setForm({ ...form, location: v })} testid="proj-loc" />
                <div>
                  <Label className="label-eyebrow">STATUS</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="bg-black border-[#222] rounded-none h-10 mt-2 font-mono"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="on-hold">On Hold</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-eyebrow">START DATE</Label>
                  <div className="mt-2"><DatePicker value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} /></div>
                </div>
                <div>
                  <Label className="label-eyebrow">END DATE</Label>
                  <div className="mt-2"><DatePicker value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} /></div>
                </div>
                <div className="sm:col-span-2">
                  <Label className="label-eyebrow">NOTES</Label>
                  <Textarea
                    value={form.notes || ""}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="bg-black border-[#222] rounded-none mt-2 font-mono text-sm"
                    rows={3}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="label-eyebrow">ASSIGNED EMPLOYEES</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto border border-[#222] p-3">
                    {employees.length === 0 && <div className="text-xs font-mono text-[#666]">No employees yet</div>}
                    {employees.map((e) => (
                      <label key={e.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={(form.assigned_employee_ids || []).includes(e.id)}
                          onChange={() => toggleEmployee(e.id)}
                          className="accent-white"
                        />
                        <span className="truncate">{e.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submit} data-testid="save-project-btn" className="rounded-none btn-brand border border-[#3b2bd0] font-heading uppercase">{editing ? "Update" : "Create"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="projects-grid">
        {projects.map((p) => (
          <Card key={p.id} className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-5 flex flex-col">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="font-heading text-lg font-bold uppercase tracking-tight truncate">{p.name}</div>
                <div className="font-mono text-xs text-[#888] mt-1">{p.client || "—"}</div>
              </div>
              <Badge variant="outline" className={`rounded-none font-mono text-xs uppercase ${statusColor(p.status)}`}>{p.status}</Badge>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-xs font-mono text-[#bbb]">
              {p.aircraft_type && <Info k="A/C TYPE" v={p.aircraft_type} />}
              {p.aircraft_registration && <Info k="REG" v={p.aircraft_registration} />}
              {p.location && <Info k="LOCATION" v={p.location} />}
              {p.start_date && <Info k="START" v={p.start_date} />}
              {p.end_date && <Info k="END" v={p.end_date} />}
              <Info k="CREW" v={(p.assigned_employee_ids || []).length} />
            </div>
            {p.notes && <div className="mt-3 text-sm text-[#aaa] line-clamp-2">{p.notes}</div>}
            {user.role === "admin" && (
              <div className="mt-4 flex gap-2 pt-3 border-t border-[#1c1c1c]">
                <Button size="sm" variant="ghost" onClick={() => startEdit(p)} className="rounded-none border border-[#222] text-xs"><Edit className="h-3 w-3 mr-1" /> Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => remove(p.id)} className="rounded-none border border-[#222] text-xs text-red-400"><Trash2 className="h-3 w-3 mr-1" /> Delete</Button>
              </div>
            )}
          </Card>
        ))}
        {projects.length === 0 && (
          <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-10 text-center md:col-span-3">
            <div className="label-eyebrow">NO PROJECTS</div>
            <div className="text-sm text-[#888] mt-2">Create your first project to start tracking manpower.</div>
          </Card>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, set, type = "text", testid }) {
  return (
    <div>
      <Label className="label-eyebrow">{label.toUpperCase()}</Label>
      <Input
        type={type}
        value={value || ""}
        onChange={(e) => set(e.target.value)}
        data-testid={testid}
        className="bg-black border-[#222] rounded-none h-10 mt-2 font-mono text-sm"
      />
    </div>
  );
}
function Info({ k, v }) {
  return (
    <div>
      <div className="label-eyebrow">{k}</div>
      <div>{v}</div>
    </div>
  );
}
